import React, { FC } from 'react';
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ChevronRight, Shield, Zap, Globe, FileImage, FileText, Download, Users, Sparkles, CheckCircle2, Star, FileSpreadsheet, FileType, ScanText, Radio, PenTool, FilePlus2, Lock, Minimize2, RefreshCw, Layers, Scan, GitMerge, Rss, PenLine, LayoutDashboard, FileEdit, BarChart3, X, Languages, MessageCircleQuestion, Tags, Smile, GitCompare, Mail, QrCode, Smartphone, Scissors, FileArchive } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { useSEO } from "@/hooks/useSEO";

// Add keyframes for animations
const style = document.createElement('style');
style.textContent = `
  @keyframes blob {
    0% { transform: translate(0px, 0px) scale(1); }
    33% { transform: translate(30px, -50px) scale(1.1); }
    66% { transform: translate(-20px, 20px) scale(0.9); }
    100% { transform: translate(0px, 0px) scale(1); }
  }
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
  }
  .animate-blob {
    animation: blob 15s infinite;
  }
  .animate-float {
    animation: float 6s ease-in-out infinite;
  }
  .animation-delay-2000 {
    animation-delay: 2s;
  }
  .animation-delay-4000 {
    animation-delay: 4s;
  }

  /* Mascot */
  @keyframes mascot-bounce {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    25% { transform: translateY(-6px) rotate(-3deg); }
    75% { transform: translateY(-3px) rotate(3deg); }
  }
  @keyframes slide-in-right {
    from { opacity: 0; transform: translateX(60px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes slide-in-left {
    from { opacity: 0; transform: translateX(-60px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes bubble-pop {
    0%   { opacity: 0; transform: scale(0.7) translateY(8px); }
    70%  { transform: scale(1.05) translateY(-2px); }
    100% { opacity: 1; transform: scale(1) translateY(0); }
  }
  @keyframes pulse-glow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(220,38,38,0.4); }
    50%       { box-shadow: 0 0 0 10px rgba(220,38,38,0); }
  }
  @keyframes pulse-glow-blue {
    0%, 100% { box-shadow: 0 0 0 0 rgba(59,130,246,0.4); }
    50%       { box-shadow: 0 0 0 10px rgba(59,130,246,0); }
  }
  .pulse-glow-blue { animation: pulse-glow-blue 2s ease-in-out infinite; }
  @keyframes tool-shine {
    0%   { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
  .mascot-bounce { animation: mascot-bounce 3s ease-in-out infinite; }
  .slide-in-right { animation: slide-in-right 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards; }
  .slide-in-left  { animation: slide-in-left  0.5s cubic-bezier(0.34,1.56,0.64,1) forwards; }
  .bubble-pop     { animation: bubble-pop 0.4s cubic-bezier(0.34,1.56,0.64,1) forwards; }
  .pulse-glow     { animation: pulse-glow 2s ease-in-out infinite; }

`;
document.head.appendChild(style);
import { Button } from "@/components/ui/button";
import { isAuthenticated } from '@/lib/auth';
import { useEffect, useState } from 'react';
import io from "socket.io-client";
import FeatureArt from '@/components/FeatureArt';
import TiltCard from '@/components/TiltCard';
import HeroRings from '@/components/HeroRings';
import HomeNewsStrip from '@/components/HomeNewsStrip';

const getApiBase = (): string => {
  return import.meta.env.VITE_API_URL || "https://slimfile-backend.onrender.com";
};

const Home: FC = () => {
  useSEO({
    title: 'SlimFile — Compress, Convert & Collaborate on Files',
    description: 'Compress PDFs, images, and Office files for free. Convert formats, summarize documents with AI, and collaborate with your team — all in one place.',
  });
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [compressedCount, setCompressedCount] = useState<number>(0);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);

  // AI Lab carousel
  const [aiLabIndex, setAiLabIndex] = useState(0);
  const aiLabFeatures = [
    { icon: Languages, title: 'Translate Text', gradient: 'from-blue-500 to-cyan-500', color: 'text-blue-600', href: '/ai-lab?feature=translate', description: 'Translate text to any language instantly with AI.', features: ['12+ languages', 'Preserves context', 'File upload support'] },
    { icon: FileText, title: 'Summarize Document', gradient: 'from-purple-500 to-pink-500', color: 'text-purple-600', href: '/ai-lab?feature=summarize', description: 'Get key points and summaries from any document.', features: ['PDF, DOCX support', 'Smart extraction', 'Structured output'] },
    { icon: PenTool, title: 'Rewrite Content', gradient: 'from-green-500 to-emerald-500', color: 'text-emerald-600', href: '/ai-lab?feature=rewrite', description: 'Improve, formalize, or simplify your text.', features: ['Multiple styles', 'Tone adjustment', 'Quality improvement'] },
    { icon: MessageCircleQuestion, title: 'Ask Questions', gradient: 'from-orange-500 to-red-500', color: 'text-orange-600', href: '/ai-lab?feature=question', description: 'Get answers about your documents and text.', features: ['Document analysis', 'Detailed answers', 'Context-aware'] },
    { icon: Tags, title: 'Extract Keywords', gradient: 'from-yellow-500 to-orange-500', color: 'text-yellow-600', href: '/ai-lab?feature=keywords', description: 'Identify key terms, topics, and tags automatically.', features: ['Topic analysis', 'Tag generation', 'Categorization'] },
    { icon: Smile, title: 'Analyze Sentiment', gradient: 'from-pink-500 to-rose-500', color: 'text-pink-600', href: '/ai-lab?feature=sentiment', description: 'Detect tone, emotions, and sentiment in text.', features: ['Emotion detection', 'Tone analysis', 'Detailed breakdown'] },
    { icon: GitCompare, title: 'Compare Texts', gradient: 'from-indigo-500 to-purple-500', color: 'text-indigo-600', href: '/ai-lab?feature=compare', description: 'Find differences and similarities between texts.', features: ['Side-by-side analysis', 'Change detection', 'Structured report'] },
    { icon: Mail, title: 'Email Generator', gradient: 'from-blue-600 to-cyan-600', color: 'text-blue-700', href: '/ai-lab?feature=email', description: 'Generate professional emails from bullet points.', features: ['8 email types', '6 tone options', 'Instant generation'] },
  ];

  // General Features (no duplicates)
  const generalFeatures = [
    { icon: FileImage,  title: "Compress Files",        description: "Shrink images, PDFs, DOCX & XLSX while keeping quality.",               features: ["JPEG, PNG, WebP, PDF", "DOCX & XLSX support", "Up to 95% reduction"],       gradient: "from-blue-500 to-blue-600", color: "text-blue-600",     href: "/compress",   flip: "Sneaky peek! 👀"          },
    { icon: FileText,  title: "Convert Formats",       description: "Transform files between formats — no quality loss.",                     features: ["Images, Office to PDF", "PDF to Images ZIP", "No quality loss"],              gradient: "from-purple-500 to-purple-600", color: "text-purple-600", href: "/convert-only",       flip: "Oh you curious one! 🐱"   },
    { icon: Zap,       title: "Convert & Compress",    description: "Convert format AND reduce size in one single step.",                     features: ["All conversion features", "Max size reduction", "One-step processing"],       gradient: "from-red-500 to-red-600", color: "text-red-600",       href: "/convert-compress", flip: "You snooped! 🕵️"          },
    { icon: FilePlus2, title: "PDF Merger & Splitter", description: "Combine multiple PDFs or split one into custom sections.",               features: ["Merge up to 20 PDFs", "Split by page ranges", "Drag to reorder"],           gradient: "from-amber-500 to-orange-500", color: "text-amber-600",  href: "/forge",       flip: "Busted! 🫢"                },
    { icon: Layers,    title: "Merge & Compress PDFs", description: "Combine PDFs into one and compress the result in a single step.",       features: ["Up to 20 PDFs", "Compressed after merging", "One file out"],          gradient: "from-teal-500 to-emerald-600", color: "text-teal-600",  href: "/merge-compress",       flip: "Two birds, one stone! 🪶"    },
    { icon: GitMerge,  title: "Split & Compress PDFs", description: "Split a PDF into pages and compress every piece at once.",            features: ["Split by page ranges", "Each piece compressed", "PDF or ZIP download"],      gradient: "from-cyan-600 to-blue-600", color: "text-cyan-700",    href: "/split-compress",       flip: "Shrink that split! ✂️"         },
    { icon: FileEdit,  title: "Text to PDF",            description: "Paste plain text and get a clean, formatted PDF in seconds.",        features: ["Headings, lists & bold", "No sign-up needed", "Print-ready A4"],            gradient: "from-emerald-500 to-teal-600", color: "text-emerald-600", href: "/text-to-pdf",       flip: "Type it, ship it! 📄"         },
    { icon: ScanText,  title: "OCR Tool",              description: "Extract editable text from images and scanned documents.",               features: ["Scan images & PDFs", "Multiple languages", "Export as text/PDF"],            gradient: "from-orange-500 to-orange-600", color: "text-orange-600", href: "/ocr-tool",       flip: "Well well well... 😏"      },
    { icon: Lock,      title: "PDF Password Protect",  description: "Lock PDFs with a password or remove existing ones.",                    features: ["128-bit encryption", "Remove passwords", "Files never stored"],              gradient: "from-violet-500 to-violet-600", color: "text-violet-600", href: "/lock",       flip: "Oh snap! 😮"               },
    { icon: FileArchive,title: "Zip Files & Folders",    description: "Bundle files or a whole folder into one ZIP, folders kept. Unzip too.",    features: ["Keeps folder paths", "Up to 5 GB", "Nothing stored"],               gradient: "from-slate-500 to-gray-600", color: "text-gray-700",    href: "/zip",         flip: "Pack it up! 🗜️"          },
    { icon: QrCode,    title: "QR Code Generator",     description: "Create QR codes for links, text, WhatsApp, WiFi & email — instantly.",   features: ["PNG & SVG download", "No sign-up needed", "100% in your browser"],         gradient: "from-fuchsia-500 to-pink-600", color: "text-fuchsia-600", href: "/qr-code",       flip: "You scanned me! 🔍"          },
    { icon: Radio,     title: "Activity Feed",         description: "Stay updated with live activity across all your workspaces.",             features: ["Real-time updates", "Activity tracking", "Team notifications"],              gradient: "from-indigo-500 to-indigo-600", color: "text-indigo-600", href: "/feed",      flip: "Look who's here! 👋"       },
    { icon: PenTool,   title: "Whiteboards",           description: "Brainstorm ideas and sketch concepts on visual boards.",                 features: ["Drawing tools", "Text & shapes", "Multiple boards"],                         gradient: "from-pink-500 to-pink-600", color: "text-pink-600",     href: "/my-whiteboards",       flip: "Peek-a-boo! 🫣"            },
    { icon: FileType,  title: "SlimDocx",              description: "A full Word-style editor for your documents.",                         features: ["Pages, tables & track changes", "Open & download Word files", "Saves automatically"],      gradient: "from-sky-500 to-sky-600", color: "text-sky-600",       href: "/documents",       flip: "Gotcha! 😏"                },
    { icon: Users,     title: "Team Workspaces",       description: "Collaborate in real-time with your team in shared spaces.",               features: ["Real-time chat", "Share links & resources", "Member management"],            gradient: "from-green-500 to-green-600", color: "text-green-600",   href: "/workspaces",      flip: "Caught ya! 😄"             },
  ];

  // Scanner tools. Kept out of generalFeatures so they appear only under the
  // Scan tab — they run off the phone camera and have nothing to do with
  // desktop uploads, so burying them in All would just be noise there.
  const scanFeatures = [
    { icon: Smartphone,  title: "Scan & Compress",   description: "Photograph pages and get one small, shareable PDF.",              gradient: "from-orange-400 to-orange-500", color: "text-orange-500", href: "/scan-compress" },
    { icon: Layers,      title: "Batch Scan & Merge", description: "Scan several documents and combine every page into one PDF.",       gradient: "from-blue-400 to-blue-500",     color: "text-blue-500",   href: "/scan-merge" },
    { icon: Scissors,    title: "Batch Scan & Split", description: "Scan a document and split it into separate small PDFs.",            gradient: "from-green-400 to-green-500",   color: "text-green-500",  href: "/scan-split" },
    { icon: RefreshCw,   title: "Scan & Convert",     description: "Turn a photo into Word, Excel, PowerPoint or images.",             gradient: "from-purple-400 to-purple-500", color: "text-purple-500", href: "/scan-convert" },
  ];

  // Category tabs, iLovePDF style. Every feature belongs to exactly one tab,
  // except "All" which is the union — so no tool appears twice. Summarize
  // Document lives only in AI Lab; it used to be duplicated in generalFeatures.
  const aiLabTab = aiLabFeatures.map(f => ({ ...f, flip: "AI Power! 🤖" }));
  const byTitle = (t: string) => generalFeatures.find(f => f.title === t)!;
  const TEAM_TITLES = new Set(["Activity Feed", "Whiteboards", "SlimDocx", "Team Workspaces"]);

  const featureTabs = [
    // Grouped deliberately: file tools, then AI Lab, then Team. Slicing off
    // the Team cards by title keeps byTitle() working off one flat array while
    // stopping collaboration tools from landing in the middle of file tools.
    { key: "all",  label: "All",     blurb: "All your file tools in one place.",
      items: [
        ...generalFeatures.filter(f => !TEAM_TITLES.has(f.title)),
        ...aiLabTab,
        ...generalFeatures.filter(f => TEAM_TITLES.has(f.title))
      ] },
    { key: "pdf",  label: "PDF",     blurb: "Merge, split, compress and protect your PDFs.",                        items: [byTitle("PDF Merger & Splitter"), byTitle("Merge & Compress PDFs"), byTitle("Split & Compress PDFs"), byTitle("Text to PDF"), byTitle("OCR Tool"), byTitle("PDF Password Protect")] },
    { key: "files", label: "Files",  blurb: "Shrink, convert and generate files.",                                  items: [byTitle("Compress Files"), byTitle("Convert Formats"), byTitle("Convert & Compress"), byTitle("Zip Files & Folders"), byTitle("QR Code Generator")] },
    { key: "scan",  label: "Scan",   blurb: "Photograph paper with your phone and turn it into a PDF.",             items: scanFeatures },
    { key: "team", label: "Team",   blurb: "Sketch, write and collaborate together.",                              items: [byTitle("Whiteboards"), byTitle("SlimDocx"), byTitle("Team Workspaces"), byTitle("Activity Feed")] },
    { key: "ai",   label: "AI Lab",  blurb: "Translate, summarise, rewrite and analyse with AI.",                   items: aiLabTab },
  ];

  const [activeTab, setActiveTab] = useState(featureTabs[0].key);
  const visibleFeatures = featureTabs.find(t => t.key === activeTab)!.items;


  useEffect(() => {
    const API_BASE = getApiBase();
    fetch(`${API_BASE}/api/stats`, { credentials: 'omit' })
      .then(res => res.json())
      .then(data => setCompressedCount(typeof data.compressedCount === 'number' ? data.compressedCount : 0))
      .catch(() => setCompressedCount(0));

    const socket = io(API_BASE, {
      transports: ['websocket'],
    });

    socket.on("statsUpdate", (newCount: number) => {
      if (typeof newCount === 'number') setCompressedCount(newCount);
    });

    return () => {
      socket.off("statsUpdate");
      socket.disconnect();
    };
  }, []);

  return (
    <div className="relative isolate min-h-screen bg-white overflow-x-hidden">
      <HeroRings />
      {/* Hero Section */}
      {/* pt-20 clears the fixed 64px header now that the purple banner is gone.
          Kept tight on mobile so ~10 slim cards fit in one viewport. */}
      <section className="pt-20 sm:pt-28 md:pt-32 pb-6 sm:pb-14 md:pb-16 px-4 sm:px-6 lg:px-8">

        <div className="container mx-auto">
          <HomeNewsStrip />
          {/* Category tabs — narrow text tabs, iLovePDF style. The old hero
              (headline, subtitle and three CTA buttons) was removed.
              Six tabs now overflow a narrow phone, so this scrolls sideways
              instead of wrapping and pushing the grid off screen. */}
          {/* Apple-style segmented control: a grey track with a white pill
              that slides to the selected tab. On narrow phones the track
              scrolls sideways instead of wrapping. */}
          <div className="-mx-4 px-4 sm:mx-0 sm:px-0 overflow-x-auto flex sm:justify-center [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="inline-flex items-center gap-0.5 p-1 rounded-full bg-[#e8e8ed]">
              {featureTabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`relative px-4 sm:px-5 py-1.5 sm:py-2 text-[13px] sm:text-sm font-medium whitespace-nowrap rounded-full transition-colors duration-200 ${
                    activeTab === tab.key ? "text-[#1d1d1f]" : "text-[#6e6e73] hover:text-[#1d1d1f]"
                  }`}
                >
                  {activeTab === tab.key && (
                    <motion.span
                      layoutId="home-tab-pill"
                      className="absolute inset-0 rounded-full bg-white shadow-[0_1px_4px_rgba(0,0,0,0.12)]"
                      transition={{ type: "spring", stiffness: 500, damping: 40 }}
                    />
                  )}
                  <span className="relative">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          <p className="text-center text-[15px] text-[#6e6e73] mt-4">
            {featureTabs.find(t => t.key === activeTab)!.blurb}
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="pt-4 pb-12 sm:pb-16 md:pb-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-[1600px]">
          {/* Arrows and dots removed — the category tabs above now drive this grid. */}
          <div className="relative">

          {/* 5 columns on wide screens, not 4. At max-w-7xl with 4 columns the cards
              were ~300px each and read as zoomed out next to iLovePDF.
              Below sm each card collapses to a single slim row — icon and title
              — so roughly 10 fit on screen without scrolling. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2 sm:gap-5">
            {visibleFeatures.map((feature) => {
              // Apple-style tile: soft grey surface with the red SlimFile artwork
              // on top, large title and a quiet "Try it" link. On phones it
              // collapses to a slim row so ~10 tools fit on one screen.
              const card = (
                <TiltCard className="group h-full flex flex-row sm:flex-col items-center sm:items-stretch gap-3 sm:gap-0 rounded-2xl sm:rounded-[28px] bg-[#f5f5f7] p-3.5 sm:p-3 sm:pb-7 transition-[box-shadow] duration-300 ease-out hover:shadow-[0_24px_50px_-20px_rgba(0,0,0,0.22)]">
                  <span className="sm:hidden"><FeatureArt icon={feature.icon} size="small" /></span>
                  <div className="hidden sm:block mb-6"><FeatureArt icon={feature.icon} /></div>
                  <div className="min-w-0 flex-1 sm:flex-none sm:px-4">
                    <p className="text-[15px] sm:text-[21px] font-semibold tracking-tight text-[#1d1d1f] leading-snug truncate sm:whitespace-normal">{feature.title}</p>
                    <p className="hidden sm:block mt-2 text-[15px] leading-relaxed text-[#6e6e73]">{feature.description}</p>
                  </div>
                  <span className="hidden sm:inline-flex items-center gap-0.5 mt-auto pt-6 sm:px-4 text-[15px] font-medium text-red-600 transition-all duration-300 group-hover:gap-1.5">
                    Try it <ChevronRight className="w-4 h-4" />
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-400 shrink-0 sm:hidden" />
                </TiltCard>
              );
              return feature.href.startsWith('http') ? (
                <a
                  key={feature.title}
                  href={feature.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block h-full"
                >
                  {card}
                </a>
              ) : (
                <Link key={feature.title} to={feature.href} className="block h-full">
                  {card}
                </Link>
              );
            })}
          </div>
          </div>
        </div>
      </section>

      {/* Office Documents Highlight Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white border-y border-gray-200">
        <div className="container mx-auto">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8 sm:mb-10 md:mb-12">
              <div className="inline-flex items-center gap-1 sm:gap-2 bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-full px-4 py-1.5 sm:px-6 sm:py-2 mb-4 sm:mb-6">
                <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="font-semibold text-xs sm:text-sm">NEW FEATURE</span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
                Microsoft Office Document Compression
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
                Now supporting Word documents and Excel spreadsheets with powerful compression
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 border-2 border-purple-200 hover:border-purple-300 transition-all duration-300 shadow-lg hover:shadow-xl">
                <div className="flex items-center gap-3 mb-4 sm:mb-6">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg sm:rounded-xl flex items-center justify-center">
                    <FileType className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900">DOCX Files</h3>
                    <p className="text-xs sm:text-sm text-gray-600">Word Documents</p>
                  </div>
                </div>
                <ul className="space-y-2 sm:space-y-3">
                  {[
                    "Compress embedded images and graphics",
                    "Remove unnecessary metadata and revision history",
                    "Works even without images through XML optimization",
                    "5-30% reduction for text-only documents",
                    "Up to 95% reduction for documents with images"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 sm:gap-3">
                      <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm sm:text-base text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 border-2 border-green-200 hover:border-green-300 transition-all duration-300 shadow-lg hover:shadow-xl">
                <div className="flex items-center gap-3 mb-4 sm:mb-6">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg sm:rounded-xl flex items-center justify-center">
                    <FileSpreadsheet className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900">XLSX Files</h3>
                    <p className="text-xs sm:text-sm text-gray-600">Excel Spreadsheets</p>
                  </div>
                </div>
                <ul className="space-y-2 sm:space-y-3">
                  {[
                    "Optimize charts and embedded images",
                    "Remove calculation chains (auto-recalculated on open)",
                    "Maximum ZIP compression for better file size",
                    "5-30% reduction for data-only spreadsheets",
                    "Up to 95% reduction for files with charts/images"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 sm:gap-3">
                      <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm sm:text-base text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-6 sm:mt-8 text-center">
              <Link to="/compress">
                <Button
                  size="lg"
                  className="text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                >
                  <span className="flex items-center gap-2">
                    Try Office Compression Now
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                  </span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* File Conversion Section - NEW */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-50 to-white border-y border-purple-100">
        <div className="container mx-auto">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8 sm:mb-10 md:mb-12">
              <div className="inline-flex items-center gap-1 sm:gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full px-4 py-1.5 sm:px-6 sm:py-2 mb-4 sm:mb-6">
                <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="font-semibold text-xs sm:text-sm">NEW FEATURES</span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
                File Conversion Tools
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
                Convert your files between different formats with our powerful conversion tools
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-8 sm:mb-10 md:mb-12">
              {/* Convert Only */}
              <div className="group relative">
                <div className="relative bg-gradient-to-br from-white to-purple-50 p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl border-2 border-purple-200 hover:border-purple-300 transition-all duration-300 transform hover:-translate-y-1 sm:hover:-translate-y-2 hover:shadow-lg sm:hover:shadow-xl h-full">
                  {/* New Badge */}
                  <div className="absolute -top-2 -right-2 sm:-top-3 sm:-right-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-2 py-0.5 sm:px-3 sm:py-1 rounded-full shadow-lg">
                    New
                  </div>

                  {/* Icon */}
                  <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-105 sm:group-hover:scale-110 transition-transform duration-300">
                    <FileText className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-white" />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                    Convert Only
                  </h3>
                  <p className="text-gray-600 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">
                    Transform your files between different formats without compression. Perfect for format compatibility.
                  </p>

                  {/* Features */}
                  <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                    {[
                      "Image formats: JPG, PNG, WebP, PDF",
                      "Office docs: DOCX, PPTX, XLSX to PDF",
                      "PDF to Images (ZIP)"
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-gray-600">
                        <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4 text-purple-500 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Link */}
                  <Link to="/convert-only">
                    <Button
                      variant="ghost"
                      className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg sm:rounded-xl px-0 transition-all duration-300 group-hover:gap-1 sm:group-hover:gap-2 text-sm sm:text-base"
                    >
                      <span className="flex items-center gap-1">
                        Try Convert Only
                        <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Convert and Compress */}
              <div className="group relative">
                <div className="relative bg-gradient-to-br from-white to-red-50 p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl border-2 border-red-200 hover:border-red-300 transition-all duration-300 transform hover:-translate-y-1 sm:hover:-translate-y-2 hover:shadow-lg sm:hover:shadow-xl h-full">
                  {/* New Badge */}
                  <div className="absolute -top-2 -right-2 sm:-top-3 sm:-right-3 bg-gradient-to-r from-red-600 to-orange-600 text-white text-xs font-bold px-2 py-0.5 sm:px-3 sm:py-1 rounded-full shadow-lg">
                    New
                  </div>

                  {/* Icon */}
                  <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 bg-gradient-to-br from-red-500 to-red-600 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-105 sm:group-hover:scale-110 transition-transform duration-300">
                    <Zap className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-white" />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                    Convert & Compress
                  </h3>
                  <p className="text-gray-600 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">
                    The ultimate two-in-one solution. Convert between formats AND optimize file size in a single step.
                  </p>

                  {/* Features */}
                  <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                    {[
                      "All conversion features included",
                      "Maximum file size reduction",
                      "Perfect quality with smaller files"
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-gray-600">
                        <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4 text-red-500 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Link */}
                  <Link to="/convert-compress">
                    <Button
                      variant="ghost"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg sm:rounded-xl px-0 transition-all duration-300 group-hover:gap-1 sm:group-hover:gap-2 text-sm sm:text-base"
                    >
                      <span className="flex items-center gap-1">
                        Try Convert & Compress
                        <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Compress Only */}
              <div className="group relative">
                <div className="relative bg-gradient-to-br from-white to-blue-50 p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl border-2 border-blue-200 hover:border-blue-300 transition-all duration-300 transform hover:-translate-y-1 sm:hover:-translate-y-2 hover:shadow-lg sm:hover:shadow-xl h-full">
                  {/* New Badge */}
                  <div className="absolute -top-2 -right-2 sm:-top-3 sm:-right-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-xs font-bold px-2 py-0.5 sm:px-3 sm:py-1 rounded-full shadow-lg">
                    New
                  </div>

                  {/* Icon */}
                  <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-105 sm:group-hover:scale-110 transition-transform duration-300">
                    <FileImage className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-white" />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                    Compress Only
                  </h3>
                  <p className="text-gray-600 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">
                    Reduce file sizes while maintaining the same format. Ideal for storage optimization and faster sharing.
                  </p>

                  {/* Features */}
                  <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                    {[
                      "Images: JPEG, PNG, WebP",
                      "Documents: PDF, DOCX, PPTX, XLSX",
                      "Up to 95% size reduction"
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-gray-600">
                        <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4 text-blue-500 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Link */}
                  <Link to="/compress">
                    <Button
                      variant="ghost"
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg sm:rounded-xl px-0 transition-all duration-300 group-hover:gap-1 sm:group-hover:gap-2 text-sm sm:text-base"
                    >
                      <span className="flex items-center gap-1">
                        Try Compress Only
                        <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            <div className="text-center">
              <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-200 w-full max-w-4xl mx-auto">
                <div className="text-center sm:text-left w-full sm:w-auto">
                  <h4 className="text-lg font-bold text-gray-900 mb-1">Choose Your Tool</h4>
                  <p className="text-sm text-gray-600">Select the perfect tool for your needs</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                  <Link to="/convert-only" className="w-full sm:w-auto">
                    <Button
                      size="sm"
                      className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white rounded-xl font-semibold transition-all shadow-md hover:shadow-lg"
                    >
                      Convert Only
                    </Button>
                  </Link>
                  <Link to="/convert-compress" className="w-full sm:w-auto">
                    <Button
                      size="sm"
                      className="w-full px-6 py-3 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 text-white rounded-xl font-semibold transition-all shadow-md hover:shadow-lg"
                    >
                      Convert & Compress
                    </Button>
                  </Link>
                  <Link to="/compress" className="w-full sm:w-auto">
                    <Button
                      size="sm"
                      className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white rounded-xl font-semibold transition-all shadow-md hover:shadow-lg"
                    >
                      Compress Only
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section - Enhanced */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8 sm:mb-10 md:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
                See It In Action
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 px-4">
                Watch how easy it is to compress your files with SlimFile
              </p>
            </div>

            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-red-600 to-orange-600 rounded-xl sm:rounded-2xl lg:rounded-3xl blur-lg opacity-25 group-hover:opacity-40 transition-opacity"></div>
              <div className="relative rounded-lg sm:rounded-xl lg:rounded-2xl overflow-hidden shadow-xl sm:shadow-2xl">
                <video
                  className="w-full h-auto"
                  controls
                  poster="/lovable-uploads/thumbnail.png"
                >
                  <source src="/lovable-uploads/Short.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>

            <div className="text-center mt-6 sm:mt-8">
              <Link to="/compress">
                <Button
                  size="lg"
                  className="text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl bg-red-600 hover:bg-red-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                >
                  <span className="flex items-center gap-2">
                    Try It Yourself
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                  </span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us - Redesigned */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
              Why Choose SlimFile?
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              The most reliable file compression platform on the web
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: Zap,
                title: "Lightning Fast",
                description: "Advanced algorithms compress your files in seconds, not minutes.",
                gradient: "from-yellow-400 to-orange-500"
              },
              {
                icon: Shield,
                title: "100% Secure",
                description: "All processing happens in your browser. Your files never leave your device.",
                gradient: "from-blue-400 to-blue-600"
              },
              {
                icon: Globe,
                title: "Works Everywhere",
                description: "Access from any device, any browser. No downloads required.",
                gradient: "from-purple-400 to-purple-600"
              }
            ].map((feature, index) => (
              <div key={feature.title} className="text-center group">
                <div className="relative bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 border-2 border-gray-100 hover:border-gray-200 transition-all duration-300 transform hover:-translate-y-1 sm:hover:-translate-y-2 shadow-lg hover:shadow-xl">
                  {/* Icon with gradient */}
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br ${feature.gradient} rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 group-hover:scale-105 sm:group-hover:scale-110 transition-transform duration-300`}>
                    <feature.icon className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" />
                  </div>

                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-base sm:text-lg leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Redesigned */}
      <section className="relative py-12 sm:py-16 md:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-red-600 via-red-700 to-red-800"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-orange-500/20 via-transparent to-transparent"></div>
        
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-white/90 mb-6 sm:mb-8 md:mb-10 px-4">
              Join thousands of users who trust SlimFile for their compression needs
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
              <Link to="/compress" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  className="text-base sm:text-lg px-6 sm:px-8 md:px-10 py-4 sm:py-5 md:py-6 rounded-xl sm:rounded-2xl bg-white text-red-600 hover:bg-gray-50 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 w-full"
                >
                  <span className="flex items-center gap-2">
                    Start Compressing Now
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                  </span>
                </Button>
              </Link>
              <Link to="/about" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  variant="outline"
                  className="text-base sm:text-lg px-6 sm:px-8 md:px-10 py-4 sm:py-5 md:py-6 rounded-xl sm:rounded-2xl bg-transparent text-white border-2 border-white/30 hover:bg-white/10 hover:border-white transition-all duration-300 w-full"
                >
                  <Users className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                  About Us
                </Button>
              </Link>
            </div>

            {/* Support Link */}
            <div className="mt-6 sm:mt-8">
              <a
                href="https://gofund.me/dcf07947"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors text-sm"
              >
                <span>Support our mission</span>
                <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Analytics Modal */}
      {analyticsOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4"
          onClick={() => setAnalyticsOpen(false)}
        >
          <div
            className="relative bg-white w-full h-full sm:rounded-2xl sm:shadow-2xl overflow-hidden sm:w-[96vw] sm:h-[94vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100 bg-white">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
                  <BarChart3 className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">SlimFile Live Analytics</p>
                  <p className="text-xs text-gray-400 hidden sm:block">Powered by Google Analytics</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Mobile scroll hint */}
                <p className="text-xs text-gray-400 sm:hidden">Scroll horizontally to explore</p>
                <button
                  onClick={() => setAnalyticsOpen(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            {/* iframe wrapper — horizontal scroll on mobile */}
            <div className="overflow-auto w-full" style={{ height: "calc(100% - 53px)" }}>
              <iframe
                src="https://datastudio.google.com/embed/reporting/336ae76d-a21e-493c-b697-d936274fcb5a/page/kIV1C"
                frameBorder="0"
                style={{ border: 0, minWidth: "800px", width: "100%", height: "100%" }}
                allowFullScreen
                sandbox="allow-storage-access-by-user-activation allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
              />
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Home;
